/**
 * ALTIORA · Client-side mirror of `core/permissions.py` ROLE_MATRIX.
 *
 * Used ONLY when the founder is impersonating a persona — we recompute the
 * "effective" permission set client-side so the UI (Sidebar, <Can>, guards)
 * hides/shows what the persona would truly see, without needing a backend
 * round-trip on every navigation.
 *
 * The backend remains the source of truth: any API call still passes through
 * the X-Altiora-Impersonate header and is authorised server-side. This client
 * mirror is purely cosmetic — it prevents leaking founder-only chrome to the
 * founder while they are simulating a persona.
 */

const PERMS_BY_PREFIX = {
  self: [
    "self.view_dashboard", "self.edit_profile", "self.view_diagnostic",
    "self.edit_diagnostic", "self.view_goals", "self.view_progress",
    "self.view_recommendations", "self.view_appointments",
    "self.view_documents", "self.view_messages", "self.view_resources",
    "self.view_subscription", "self.export_own_data",
  ],
  pro: [
    "pro.view_clients", "pro.view_client_diagnostic",
    "pro.write_recommendations", "pro.manage_appointments",
    "pro.write_reports", "pro.view_own_dashboard",
  ],
  inst: [
    "inst.view_authorized_members", "inst.view_authorized_reports",
    "inst.view_authorized_stats",
  ],
  admin: [
    "admin.view_members", "admin.manage_members", "admin.view_professionals",
    "admin.manage_professionals", "admin.view_appointments",
    "admin.manage_content", "admin.manage_incidents",
    "admin.validate_documents", "admin.view_audit_light",
  ],
  founder: [
    "founder.view_global_stats", "founder.view_global_reports",
    "founder.view_finances", "founder.view_governance", "founder.view_all_partners",
    "founder.configure_ai", "founder.manage_api", "founder.system_settings",
  ],
};

const MEMBER_PERMS = PERMS_BY_PREFIX.self;
const PRO_PERMS = [...MEMBER_PERMS, ...PERMS_BY_PREFIX.pro];
const INST_PERMS = [...MEMBER_PERMS, ...PERMS_BY_PREFIX.inst];
const ADMIN_PERMS = [
  ...MEMBER_PERMS, ...PERMS_BY_PREFIX.admin, "pro.view_own_dashboard",
];
const FOUNDER_PERMS = [
  ...MEMBER_PERMS, ...PERMS_BY_PREFIX.pro, ...PERMS_BY_PREFIX.inst,
  ...PERMS_BY_PREFIX.admin, ...PERMS_BY_PREFIX.founder,
];

export const ROLE_MATRIX_CLIENT = {
  founder: new Set(FOUNDER_PERMS),
  super_admin: new Set(FOUNDER_PERMS),
  admin: new Set(ADMIN_PERMS),
  professional: new Set(PRO_PERMS),
  professionnel: new Set(PRO_PERMS),
  institution: new Set(INST_PERMS),
  user: new Set(MEMBER_PERMS),
  member: new Set(MEMBER_PERMS),
  membre: new Set(MEMBER_PERMS),
  visitor: new Set(),
};

export const PERSONA_TO_ROLE = {
  founder: "founder",
  admin: "admin",
  user: "user",
  pro: "professional",
  institution: "institution",
};

export function computeEffectivePermissions(realPermissions, personaKey) {
  // If not impersonating (or in founder mode) → keep real permissions
  if (!personaKey || personaKey === "founder") {
    return new Set(realPermissions || []);
  }
  const role = PERSONA_TO_ROLE[personaKey];
  return new Set([...(ROLE_MATRIX_CLIENT[role] || new Set())]);
}

export function computeEffectiveRoles(realRoles, personaKey) {
  if (!personaKey || personaKey === "founder") {
    return realRoles || [];
  }
  const role = PERSONA_TO_ROLE[personaKey];
  return role ? [role] : (realRoles || []);
}
